import React, { useEffect, useMemo, useRef, useState } from "react";
import { auctionV2ApiClient } from "../../auctions-list-page/AuctionApiClient";
import { AuctionClassTypes, AuctionItemStatus, } from "../../auctions-list-page/contracts";
import Spinner from "../../common/Spinner";
import { useBidAllContext } from "../../contexts/BidAllContext";
import events from "../../events";
import getAuctionHub from "../../getAuctionHub";
import { formatDateUtc, formatDateTime } from "../../utils/formatDate";
import formatPrice, { formatForeignCurrency } from "../../utils/formatPrice";
import { getTimeRemainingSecond } from "./auctionBidTimeFormat";
import BidLead from "./BidLead";
import styles from "./BidModule.module.scss";
import { BuyNowButton } from "./BuyNowButton";
import { getAvailableMaxBids } from "./getAvailableMaxBids";
import currenciesService from "../../currenciesService";
import LiveAuctionButton from "../../live-auction/LiveAuctionButton";
import { useLiveAuction } from "../../live-auction/useLiveAuction";
const TimerHoursThreshold = 24;
const CHRISTIANIA_TEST_TEXT_COLOR = "#C8A564";
const CHRISTIANIA_TEST_BG_COLOR = "#2D2D2D";
export default function BidModule(props) {
    var _a, _b, _c, _d, _e;
    const [timeCounter, setTimeCounter] = useState([]);
    const [showTimer, setShowTimer] = useState(false);
    const [foreignCurrency, setForeignCurrency] = useState(currenciesService.getRate());
    const [item, setItem] = useState(props.bidItem);
    const [bidLoading, setBidLoading] = useState(false);
    // When the live auction of this lot ends, the lot fetches itself again, exactly as it does when its own countdown
    // reaches zero. The live auction link stays until this is done.
    const liveAuction = useLiveAuction(props.bidItem.auctionId, () => refreshAfterAuctionEnded());
    // Read by `bid`, which "bid all" keeps calling from the render it was registered in.
    const isLiveAuctionRef = useRef(liveAuction.isLive);
    isLiveAuctionRef.current = liveAuction.isLive;
    const [isChristianiaTest, setIsChristianiaTest] = useState(false);
    useEffect(() => {
        const checkClass = () => {
            setIsChristianiaTest(document.body.classList.contains('christiania-test'));
        };
        checkClass();
        const observer = new MutationObserver(checkClass);
        observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
        return () => observer.disconnect();
    }, []);
    const itemRef = useRef(item);
    const latestStartTimeRef = useRef();
    const currentTimeSecRef = useRef(0);
    const animationFrameRef = useRef(0);
    // Check if auctionType equals COMBINED
    const isItemCombinedType = item.auctionType != undefined &&
        item.auctionType == AuctionClassTypes.COMBINED.toString();
    const availableMaxBids = useMemo(() => {
        var _a;
        return getAvailableMaxBids(item.currentBidPrice, item.bidsCount, (_a = item.customerBid) === null || _a === void 0 ? void 0 : _a.maxBidPrice);
    }, [item]);
    const onCurrencyChange = () => {
        setForeignCurrency(currenciesService.getRate());
    };
    // Set when the lot was just fetched because its auction ended, so the registration going away right after does
    // not fetch it a second time.
    const fetchedAfterAuctionEndRef = useRef(false);
    const wasRegisteredRef = useRef(liveAuction.isRegistered);
    const refreshAfterAuctionEnded = async () => {
        for (let attempt = 0; attempt < 2; attempt++) {
            try {
                const data = await auctionV2ApiClient.getItem(itemRef.current.id);
                fetchedAfterAuctionEndRef.current = true;
                setItem(data);
                setTimeCounter([]);
                return;
            }
            catch (error) {
                console.error(error);
            }
        }
    };
    const updateTimeCounter = (timeStamp) => {
        if (itemRef.current.secondsTillEnd == null)
            return;
        // is it the first iteration in this cycle?
        if (latestStartTimeRef.current == undefined) {
            setTimeCounter(getTimeRemainingSecond(itemRef.current.secondsTillEnd));
            latestStartTimeRef.current = timeStamp + (itemRef.current.secondsTillEnd * 1000);
        }
        const currentTimeMs = latestStartTimeRef.current - timeStamp;
        if (currentTimeMs <= 0) {
            window.cancelAnimationFrame(animationFrameRef.current);
            auctionV2ApiClient.getItem(item.id).then(data => {
                setItem(data);
                setTimeCounter([]);
            });
            return;
        }
        // the time to display now
        const currentTimeSec = Math.floor(currentTimeMs / 1000);
        if (currentTimeSecRef.current != currentTimeSec) {
            setTimeCounter(getTimeRemainingSecond(currentTimeSec));
            currentTimeSecRef.current = currentTimeSec;
        }
        animationFrameRef.current = window.requestAnimationFrame(updateTimeCounter);
    };
    const bid = async (lotId, chosenMaxBid) => {
        // Last line of defence: a lot of a started live auction is never bid on from skanfil.no.
        if (isLiveAuctionRef.current)
            return;
        if (!chosenMaxBid)
            return;
        if (chosenMaxBid < item.currentBidPrice)
            return;
        if (!document.body.dataset.username) {
            props.saveItemPositionState(lotId);
            events.openLogin.trigger({ open: true, centeredFixed: true });
            return;
        }
        setBidLoading(true);
        try {
            // item will be refreshed on BidPlaced message from SignalR
            await auctionV2ApiClient.bidLot(lotId, {
                maximumAmount: chosenMaxBid,
            });
        }
        catch (error) {
            console.error(error);
        }
    };
    const bidPlacedHandler = async (bidPlaced) => {
        setItem(await auctionV2ApiClient.getItem(bidPlaced.id));
        setBidLoading(false);
    };
    const lotUpdatedHandler = async (data) => {
        setItem((prev) => {
            return {
                ...prev,
                ...data
            };
        });
    };
    useEffect(() => {
        itemRef.current = item;
        props.onItemChanged(item);
        if (!item.secondsTillEnd) {
            setShowTimer(false);
            return;
        }
        const hours = item.secondsTillEnd / 3600;
        const show = !isItemCombinedType &&
            hours < TimerHoursThreshold &&
            !item.isAuctionHallItem &&
            // Nobody knows how long the live auction lasts, so there is nothing to count down to.
            !liveAuction.isRegistered &&
            item.secondsTillEnd > 0;
        // Clear latest start time so in next animation frame we'll get new time
        latestStartTimeRef.current = undefined;
        setShowTimer(show);
    }, [item, liveAuction.isRegistered]);
    useEffect(() => {
        const wasRegistered = wasRegisteredRef.current;
        wasRegisteredRef.current = liveAuction.isRegistered;
        if (!wasRegistered || liveAuction.isRegistered)
            return;
        if (fetchedAfterAuctionEndRef.current) {
            fetchedAfterAuctionEndRef.current = false;
            return;
        }
        // The registration was deleted while the page was open. The countdown starts from the time left now, so the
        // lot is read again (the data it was loaded with is as old as the page).
        void refreshAfterAuctionEnded().then(() => {
            fetchedAfterAuctionEndRef.current = false;
        });
    }, [liveAuction.isRegistered]);
    useEffect(() => {
        if (!showTimer) {
            window.cancelAnimationFrame(animationFrameRef.current);
            return;
        }
        animationFrameRef.current = window.requestAnimationFrame(updateTimeCounter);
    }, [showTimer]);
    useEffect(() => {
        setItem(props.bidItem);
    }, [props.bidItem]);
    useEffect(() => {
        const unwatchPromise = getAuctionHub().then((hub) => {
            return hub === null || hub === void 0 ? void 0 : hub.watchLot(props.bidItem.id, bidPlacedHandler, lotUpdatedHandler);
        });
        const sub = events.onCurrencyChanged.subscribe(onCurrencyChange);
        return () => {
            sub.unSubscribe();
            window.cancelAnimationFrame(animationFrameRef.current);
            unwatchPromise.then((unwatch) => {
                if (unwatch) {
                    void unwatch();
                }
            });
        };
    }, []);
    const isWaiting = item.status == AuctionItemStatus.Prepared
        || item.status == AuctionItemStatus.Scheduled;
    const biddingHasEnded = ((_a = item.endAt) !== null && _a !== void 0 ? _a : "z") < new Date().toISOString();
    const isBiddable = item.status == AuctionItemStatus.Available && !biddingHasEnded && item.isAvailable;
    const isAftersale = item.status == AuctionItemStatus.NotSold && props.auctionStatus === 5;
    const isSold = item.status == AuctionItemStatus.Sold
        || item.status == AuctionItemStatus.SoldConfirmed
        || item.status === AuctionItemStatus.Completed;
    const showBidStart = isWaiting && new Date((_b = item.startAt) !== null && _b !== void 0 ? _b : 0) > new Date(0);
    const showBidEnd = new Date((_c = item.endAt) !== null && _c !== void 0 ? _c : 0) > new Date(0);
    const showCurrentBidPrice = !isWaiting && item.isAvailable;
    const showCustomerMaxBid = item.customerBid && isBiddable;
    const showBidCount = !isBiddable && item.isAvailable && !isAftersale;
    const showEstimatedPrice = showCurrentBidPrice && !isSold;
    return (React.createElement("div", { className: styles.bidModule },
        showBidStart && (React.createElement("span", null,
            props.strings.biddingStartsAt,
            ":",
            " ",
            React.createElement("strong", null, GetFormattedDateForType(item.startAt, isItemCombinedType)))),
        isBiddable && showTimer && (React.createElement("div", { className: "auction-timer" }, timeCounter.map((time) => (React.createElement("span", { className: "time", key: time }, time))))),
        showBidEnd && (React.createElement("span", null,
            biddingHasEnded
                ? props.strings.biddingEndedAt
                : props.strings.endLabelList,
            ":",
            " ",
            React.createElement("strong", null, GetFormattedDateForType(item.endAt, isItemCombinedType)))),
        showEstimatedPrice && (React.createElement("span", null,
            props.strings.estimatedPriceLabel,
            ":",
            " ",
            React.createElement("strong", null, formatPrice(item.estimatedPrice)),
            React.createElement("span", { className: styles.foreignCurrency }, foreignCurrency &&
                displayForeignCurrency(foreignCurrency) &&
                formatForeignCurrency(item.estimatedPrice, foreignCurrency)))),
        showCurrentBidPrice && !isAftersale && (React.createElement("span", null,
            isSold
                ? props.strings.soldForPriceLabel
                : props.strings.startingBidPriceLabel,
            ":",
            " ",
            React.createElement("strong", { className: styles.bidCurrent, style: isChristianiaTest ? { color: CHRISTIANIA_TEST_TEXT_COLOR } : undefined },
                item.bidsCount > 0 &&
                    formatPrice(item.currentBidPrice),
                item.bidsCount == 0 && props.strings.noBids),
            React.createElement("span", { className: styles.foreignCurrency }, item.bidsCount > 0 &&
                foreignCurrency &&
                foreignCurrency.currency !== "NOK" &&
                formatForeignCurrency(item.currentBidPrice, foreignCurrency)))),
        showCustomerMaxBid && (React.createElement("span", null,
            props.strings.maxBid,
            ":",
            " ",
            React.createElement("strong", null, formatPrice((_d = item.customerBid) === null || _d === void 0 ? void 0 : _d.maxBidPrice)))),
        showBidCount && (React.createElement("span", null,
            props.strings.bidCount,
            ": ",
            React.createElement("strong", null, item.bidsCount))),
        React.createElement(BidLead, { strings: props.strings, isHighestBid: (_e = item.customerBid) === null || _e === void 0 ? void 0 : _e.hasWinningBid, isSold: isSold }),
        isBiddable && !props.biddingDisabled && liveAuction.ready && (React.createElement(BidSelector, { isLiveAuction: liveAuction.isLive, lotId: item.id, strings: props.strings, currentBidPrice: item.currentBidPrice, bidsCount: item.bidsCount, availableMaxBids: availableMaxBids, onBid: bid, foreignCurrency: foreignCurrency, isLoading: bidLoading, isChristianiaTest: isChristianiaTest })),
        isAftersale && (React.createElement("span", { className: styles.aftersaleInfo, style: isChristianiaTest ? { color: CHRISTIANIA_TEST_TEXT_COLOR } : undefined },
            React.createElement("strong", null, props.strings.aftersaleInfo))),
        isAftersale && (React.createElement(BuyNowButton, { lotId: item.id, objectNumber: item.number.toString(), strings: props.strings, price: item.estimatedPrice, onBuyNow: props.onBuyNow, saveItemPositionState: props.saveItemPositionState }))));
}
export function BidSelector({ lotId, currentBidPrice, availableMaxBids, strings, onBid, foreignCurrency, isLoading, isChristianiaTest, isLiveAuction, }) {
    const bidAllCtx = useBidAllContext();
    const [chosenMaxBid, setChosenMaxBid] = useState(0);
    const isMounted = useRef(false);
    const selectedTooltip = chosenMaxBid &&
        foreignCurrency &&
        displayForeignCurrency(foreignCurrency)
        ? formatForeignCurrency(chosenMaxBid, foreignCurrency)
        : undefined;
    const canBid = chosenMaxBid >= currentBidPrice;
    const onChangeHandle = (event) => {
        var _a, _b;
        let value = parseInt((_b = (_a = event.target) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : 0);
        if (!value || isNaN(value)) {
            value = 0;
        }
        setChosenMaxBid(value);
    };
    const optionTooltip = (maxbid) => {
        return foreignCurrency && displayForeignCurrency(foreignCurrency)
            ? formatForeignCurrency(maxbid, foreignCurrency)
            : undefined;
    };
    useEffect(() => {
        if (isMounted.current) {
            // Live auction lots are not bid on from here, so "bid all" must not include them.
            chosenMaxBid > 0 && !isLiveAuction
                ? bidAllCtx === null || bidAllCtx === void 0 ? void 0 : bidAllCtx.subscribe(lotId, () => onBid(lotId, chosenMaxBid))
                : bidAllCtx === null || bidAllCtx === void 0 ? void 0 : bidAllCtx.unsubscribe(lotId);
        }
    }, [chosenMaxBid, isLiveAuction]);
    useEffect(() => {
        isMounted.current = true;
    }, []);
    return (React.createElement("div", { className: styles.bidAction },
        React.createElement("select", { className: styles.bidInterval, onChange: (event) => onChangeHandle(event), title: selectedTooltip },
            React.createElement("option", { defaultValue: "" }, strings.maxBid),
            availableMaxBids.map((maxbid) => (React.createElement("option", { value: maxbid, key: maxbid, title: optionTooltip(maxbid) }, formatPrice(maxbid))))),
        isLiveAuction ? (React.createElement(LiveAuctionButton, { lotId: lotId, label: strings.openLiveAuctionBtnLabel, price: chosenMaxBid || null, style: isChristianiaTest ? {
                borderColor: CHRISTIANIA_TEST_BG_COLOR,
                backgroundColor: CHRISTIANIA_TEST_BG_COLOR,
                color: "white",
            } : undefined })) : (React.createElement("button", { className: styles.bidButton, disabled: !canBid || isLoading, onClick: () => onBid(lotId, chosenMaxBid), style: isChristianiaTest ? {
                borderColor: CHRISTIANIA_TEST_BG_COLOR,
                backgroundColor: canBid && !isLoading ? CHRISTIANIA_TEST_BG_COLOR : undefined,
                color: canBid && !isLoading ? "white" : CHRISTIANIA_TEST_BG_COLOR,
            } : undefined },
            React.createElement("span", { style: { visibility: isLoading ? "collapse" : "visible" } }, strings.bidBtnLabel),
            isLoading && (React.createElement(Spinner, { size: "mini", position: "absolute-center" }))))));
}
function displayForeignCurrency(currency) {
    return currency.currency !== "NOK";
}
// Differs date format ("date + time" or "date") on base of AuctionType
function GetFormattedDateForType(dateTime, isItemCombinedType) {
    if (isItemCombinedType) {
        return formatDateUtc(dateTime);
    }
    return formatDateTime(dateTime);
}
